import {
  GymThunkAction,
  SET_ALL_GYM_ROOMS,
  SET_GYM_EVENTS,
  SET_MY_GYM_ROOM,
  SetAllGymRoomsActionType,
  SetGymEventsActionType,
  SetMyGymRoomActionType,
} from './GymActionTypes';
import GymApi from '../../API/gym/Gym.api';
import { Event } from '../events/eventTypes';
import { AUTH_NAMESPACE } from '../Auth/authReducer';
import { GymResource, saveGymResource } from './GymTypes';
import RoomManagementApi from '../../API/room/RoomManagement.api';
import { startLoaderThunk, stopLoaderThunk } from '../modal/modalActions';
import { IsaveResource } from '../RoomManagement/resourceManagementTypes';
import { UpdateMedicalFormRequestDto } from '../../API/gym/dto/request/update-medical-form.request.dto';
import { UpdateMedicalStatusRequestDto } from '../../API/gym/dto/request/update-medical-status.request.dto';
import { SendMedicalDeclarationRequestDto } from '../../API/gym/dto/request/send-medical-declaration.request.dto';
import { getTimeStamp } from '../../utility/functions/getTimeString';
import { stringHour } from '../../utility/functions/stringHour';
import { FetchMyActivitiesThunk } from '../my-activities/myActivitiesActions';
import { MyActivitiesType } from '../my-activities/myActivitiesTypes';

export function setEventsAction(gymEvents: Event[]): SetGymEventsActionType {
  return {
    type: SET_GYM_EVENTS,
    payload: gymEvents,
  };
}

export function setMyGymRoomAction(
  gymRoom: GymResource
): SetMyGymRoomActionType {
  return {
    type: SET_MY_GYM_ROOM,
    payload: gymRoom,
  };
}

export function setAllGymRoomsAction(
  gymRooms: GymResource[]
): SetAllGymRoomsActionType {
  return {
    type: SET_ALL_GYM_ROOMS,
    payload: gymRooms,
  };
}

export function FetchMyGymRoomThunk(): GymThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    const profileState = getState()[AUTH_NAMESPACE];
    dispatch(startLoaderThunk()).finally();
    GymApi.getGymInMyResource(profileState.profile?.locationId || 0)
      .then((response) => {
        dispatch(setMyGymRoomAction(response.data));
      })
      .finally(() => dispatch(stopLoaderThunk()));
  };
}

export function FetchAllGymRoomsThunk(): GymThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    GymApi.getAllGyms()
      .then((response) => {
        dispatch(setAllGymRoomsAction(response.data));
      })
      .finally(() => dispatch(stopLoaderThunk()));
  };
}

export function BookGymRoomThunk(data: saveGymResource): GymThunkAction<
  Promise<{
    isPaid: boolean;
    paymentProcessLink?: string;
    orderId: number;
  }>
> {
  return async (dispatch, getState) => {
    const creditState = getState()['CREDITS_NAMESPACE'];
    const creditCardsState = getState()['CREDIT_CARDS_NAMESPACE'];
    const profile = getState()[AUTH_NAMESPACE];

    await dispatch(startLoaderThunk());

    const newAllocation: IsaveResource = {
      resource_id: data.resourceId,
      start_time: getTimeStamp(
        new Date(data.date),
        stringHour(data.startTime.toString())
      ),
      end_time: getTimeStamp(
        new Date(data.date),
        stringHour(data.endTime.toString())
      ),
      user_id: profile.profile?.id,
      company_id: profile.profile?.companyId!,
      number_of_participants: data.numberOfParticipants,
    };

    const creditDto = {
      credit_payments: creditState.creditInUse?.activeCredits?.map((credit) => {
        return {
          credit_balance_id: credit.id,
          credit_amount: credit.amount,
        };
      }),
      usage: creditState.creditInUse?.creditsAmount || undefined,
      credit_amount: creditState.creditInUse?.creditsAmount || undefined,
      cash_amount: creditState.creditInUse?.cashAmount || undefined,
      credit_card_id: creditCardsState.selectedCreditCard?.id,
      save_credit_card: creditCardsState.saveCreditCard,
    };

    try {
      const response = await RoomManagementApi.saveResourceAllocation(
        newAllocation,
        creditDto
      );
      const resData = response.data;

      return {
        isPaid: resData.paymentsProcess?.isPaid,
        paymentProcessLink: resData.paymentsProcess?.paymentProcessLink,
        orderId: resData.id,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        'Server error please try later';
      throw errorMessage;
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}

export function updateGymRoomThunk(
  data: saveGymResource
): GymThunkAction<Promise<any>> {
  return async (dispatch, getState) => {
    const creditState = getState()['CREDITS_NAMESPACE'];
    const creditCardsState = getState()['CREDIT_CARDS_NAMESPACE'];
    const profile = getState()[AUTH_NAMESPACE];

    dispatch(startLoaderThunk()).finally();
    const newDate: IsaveResource = {
      resource_id: data.resourceId,
      start_time: getTimeStamp(
        new Date(data.date),
        stringHour(data.startTime.toString())
      ),
      end_time: getTimeStamp(
        new Date(data.date),
        stringHour(data.endTime.toString())
      ),
      user_id: profile.profile?.id,
      company_id: profile.profile?.companyId!,
      number_of_participants: data.numberOfParticipants,
      resource_alloc_id: data.allocId,
    };

    const creditDto = {
      credit_payments: creditState.creditInUse?.activeCredits?.map((credit) => {
        return {
          credit_balance_id: credit.id,
          credit_amount: credit.amount,
        };
      }),
      usage: creditState.creditInUse?.creditsAmount || undefined,
      credit_amount: creditState.creditInUse?.creditsAmount || undefined,
      cash_amount: creditState.creditInUse?.cashAmount || undefined,
      credit_card_id: creditCardsState.selectedCreditCard?.id,
      save_credit_card: creditCardsState.saveCreditCard,
    };

    try {
      const response = await RoomManagementApi.updateResourceAllocation(
        newDate,
        creditDto
      );
      await dispatch(FetchMyActivitiesThunk(MyActivitiesType.FUTURE, 12));

      return {
        isSuccessfull: true,
        errorMessage: null,
        isPaid: response.data.paymentsProcess.isPaid,
        paymentProcessLink: response.data.paymentsProcess.paymentProcessLink,
        orderId: response.data.id,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message || 'Unavailbale at this time';
      return {
        isSuccessfull: false,
        errorMessage,
        isPaid: false,
        orderId: 0,
      };
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}

export function sendMedicalDeclarationThunk(
  policy: SendMedicalDeclarationRequestDto,
  didGymTour?: boolean
): GymThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return GymApi.sendMedicalDeclaration({
      ...policy,
      did_gym_tour: didGymTour,
    })
      .then((response) => {})
      .catch((e) => {
        throw e;
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function UploadMedicalCertificateThunk(
  data: any,
  callback: any
): GymThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    return GymApi.uploadMedicalCertificate(data)
      .then(async (response) => {
        callback(response.data.imageUrl, null);
      })
      .catch(() => {
        callback(null, 'Something went wrong...');
      });
  };
}

export function updateMedicalFormThunk(
  medicalForm: UpdateMedicalFormRequestDto
): GymThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return GymApi.updateMedicalForm(medicalForm)
      .then((response) => {})
      .catch((e) => {
        throw e;
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function updateMedicalStatus(
  medicalStatus: UpdateMedicalStatusRequestDto
): GymThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return GymApi.updateMedicalStatus(medicalStatus)
      .then((response) => {})
      .catch((e) => {
        throw e;
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}
